import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { canManageUserRecords } from '../utils/userManagementPermissions';
import { hasAdminPermission } from '../utils/adminPermissions';
import {
  Users,
  UserPlus,
  Search,
  Mail,
  Phone,
  Edit,
  X,
  Check,
  FileText,
  Key,
  Droplet,
  Wallet,
  CreditCard,
  BarChart3,
  Monitor,
  AlertCircle,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  Loader2
} from 'lucide-react';
import LogViewerModal from './LogViewerModal';
import { AdminModalSurface } from './AdminModal';
import { getRechargeCreditPresentation } from '../utils/rechargeCreditStatus';
import { APP_USER_ROLES, normalizeOwnedUserRoles } from '../utils/userRoles';

const getPaymentProviderLabel = (t, provider) => {
  const normalized = String(provider || '').trim().toLowerCase();
  if (normalized === 'manual') return t('transactions.paymentProvider.headquartersProvided');
  return provider || 'N/A';
};

export default function UserManagement() {
  const { t } = useTranslation();
  const currentAdmin = useAuthStore((state) => state.user);
  const canManageUsers = canManageUserRecords(currentAdmin);
  const canManagePartnerBindings = hasAdminPermission(currentAdmin, 'manage_partner_bindings');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportError, setExportError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [editingUserId, setEditingUserId] = useState(null);
  const [roleDrafts, setRoleDrafts] = useState({});
  const [savingRoleUserId, setSavingRoleUserId] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', phoneNumber: '', email: '', role: 'User' });
  const [viewingLogsUserId, setViewingLogsUserId] = useState(null);
  const [viewingLogsUserName, setViewingLogsUserName] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);

  // Password-change state.
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Detail-modal state.
  const [waterConsumptionModalOpen, setWaterConsumptionModalOpen] = useState(false);
  const [rechargeModalOpen, setRechargeModalOpen] = useState(false);
  const [cardsModalOpen, setCardsModalOpen] = useState(false);
  const [waterTransactions, setWaterTransactions] = useState([]);
  const [recharges, setRecharges] = useState([]);
  const [userCards, setUserCards] = useState({ virtualRfid: '', physicalCards: [], electronicCards: [] });
  const [analysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [analysisUser, setAnalysisUser] = useState(null);
  const [userAnalysis, setUserAnalysis] = useState(null);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [loadingRecharges, setLoadingRecharges] = useState(false);
  const [loadingCards, setLoadingCards] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  // Device-selection modal state.
  const [deviceSelectionModalOpen, setDeviceSelectionModalOpen] = useState(false);
  const [targetRole, setTargetRole] = useState('');
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);

  const ALL_ROLES = APP_USER_ROLES;
  const ROLE_DESCRIPTIONS = {
    User: 'Customer wallet and water purchase access.',
    Steward: 'Station steward workspace access.',
    KUD: 'Partner PA workspace access.',
    PUSKUD: 'Regional Partner RP workspace access.'
  };
  const SAME_ACCOUNT_POLICY_DEFAULT = 'Partner PA and Regional Partner RP can belong to the same person. Bind each role to devices separately in Units.';

  const canViewUserLogs = [
    currentAdmin?.role,
    currentAdmin?.activeRole,
    ...(Array.isArray(currentAdmin?.roles) ? currentAdmin.roles : [])
  ].includes('Super-Admin');

  const loadUsers = useCallback(async ({ page = 1, limit = pagination.limit } = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        q: searchTerm.trim() || undefined,
        role: roleFilter === 'All' ? undefined : roleFilter
      };
      const res = await adminAPI.getUsers(params);
      if (res.data && res.data.success) {
        const list = Array.isArray(res.data.data) ? res.data.data : [];
        setUsers(list);
        setPagination(res.data.pagination || {
          page,
          limit,
          total: list.length,
          totalPages: 1
        });
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, roleFilter, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => loadUsers({ page: 1 }), searchTerm.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadUsers, searchTerm]);

  const exportCurrentUsers = async () => {
    if (exportingCsv) return;

    setExportingCsv(true);
    setExportError('');
    try {
      const params = {
        q: searchTerm.trim() || undefined,
        role: roleFilter === 'All' ? undefined : roleFilter
      };
      const response = await adminAPI.exportUsersCsv(params);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ATMWater_Users_Roles.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setExportError(t('userManagement.export.error'));
    } finally {
      setExportingCsv(false);
    }
  };

  const normalizeUserRoles = (user) => normalizeOwnedUserRoles(user, ALL_ROLES);

  const beginRoleEdit = (user) => {
    setEditingUserId(user.id);
    setRoleDrafts(prev => ({
      ...prev,
      [user.id]: normalizeUserRoles(user)
    }));
  };

  const cancelRoleEdit = (userId) => {
    setEditingUserId(null);
    setRoleDrafts(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const toggleRoleDraft = (user, role) => {
    if (role === 'User' || savingRoleUserId === user.id) return;

    setRoleDrafts(prev => {
      const currentRoles = prev[user.id] || normalizeUserRoles(user);
      const nextRoleSet = new Set(currentRoles);

      if (nextRoleSet.has(role)) {
        nextRoleSet.delete(role);
      } else {
        nextRoleSet.add(role);
      }
      nextRoleSet.add('User');

      return {
        ...prev,
        [user.id]: ALL_ROLES.filter(item => nextRoleSet.has(item))
      };
    });
  };

  const roleKey = (roles) => [...roles].sort().join('|');

  const isRoleDraftDirty = (user) => {
    const draftRoles = roleDrafts[user.id] || normalizeUserRoles(user);
    return roleKey(draftRoles) !== roleKey(normalizeUserRoles(user));
  };

  const getActiveRoleForSave = (user, roles) => {
    const activeRole = user.activeRole === 'RP' ? 'KUD' : user.activeRole;
    const currentRole = user.role === 'RP' ? 'KUD' : user.role;

    if (activeRole && roles.includes(activeRole)) return activeRole;
    if (currentRole && roles.includes(currentRole)) return currentRole;
    return roles.find(role => role !== 'User') || 'User';
  };

  const handleRolesSave = async (user) => {
    if (!canManageUsers) return;
    const roles = roleDrafts[user.id] || normalizeUserRoles(user);

    setSavingRoleUserId(user.id);

    try {
      const res = await adminAPI.updateUserRoles(user.id, {
        roles,
        activeRole: getActiveRoleForSave(user, roles)
      });

      if (res.data.success) {
        await loadUsers();
        cancelRoleEdit(user.id);
        alert(t('userManagement.alerts.updateRolesSuccess', { defaultValue: 'Roles updated successfully!' }));
      }
    } catch (error) {
      alert(error.response?.data?.message || t('userManagement.alerts.updateError'));
    } finally {
      setSavingRoleUserId(null);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!canManageUsers) return;
    setLoading(true);
    try {
      const res = await adminAPI.createUser({
        phoneNumber: newUser.phoneNumber,
        name: newUser.name,
        role: newUser.role,
        email: newUser.email
      });
      if (res.data.success) {
        alert(t('userManagement.alerts.createSuccess'));
        setIsModalOpen(false);
        setNewUser({ name: '', phoneNumber: '', email: '', role: 'User' });
        loadUsers();
      }
    } catch (error) {
      alert(error.response?.data?.message || t('userManagement.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      alert(t('userManagement.passwordMismatch'));
      return;
    }

    if (newPassword.length < 6) {
      alert(t('userManagement.passwordTooShort'));
      return;
    }

    try {
      const res = await adminAPI.updateUserPassword(selectedUser.id, newPassword);
      if (res.data.success) {
        alert(t('userManagement.passwordChanged'));
        setPasswordModalOpen(false);
        setNewPassword('');
        setConfirmPassword('');
        setSelectedUser(null);
      }
    } catch (error) {
      alert(error.response?.data?.message || t('userManagement.passwordChangeFailed'));
    }
  };

  // Load user water-dispense transactions.
  const loadWaterTransactions = async (userId) => {
    setLoadingTransactions(true);
    try {
      const res = await adminAPI.getUserTransactions(userId, 'dispense');
      if (res.data.success) {
        setWaterTransactions(res.data.data.transactions || []);
      }
    } catch (error) {
      console.error('Failed to load water transactions:', error);
      alert(t('userManagement.loadDispenseError'));
    } finally {
      setLoadingTransactions(false);
    }
  };

  const loadRecharges = async (userId) => {
    setLoadingRecharges(true);
    try {
      const res = await adminAPI.getUserRecharges(userId);
      if (res.data.success) {
        setRecharges(res.data.data.recharges || []);
      }
    } catch (error) {
      console.error('Failed to load recharges:', error);
      alert(t('userManagement.loadRechargeError'));
    } finally {
      setLoadingRecharges(false);
    }
  };

  // Load user-bound cards.
  const loadUserCards = async (userId) => {
    setLoadingCards(true);
    try {
      const res = await adminAPI.getUserCards(userId);
      if (res.data.success) {
        setUserCards(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load cards:', error);
      alert(t('userManagement.loadCardError'));
    } finally {
      setLoadingCards(false);
    }
  };

  const loadUserAnalysis = async (user) => {
    setAnalysisUser(user);
    setAnalysisModalOpen(true);
    setUserAnalysis(null);
    setLoadingAnalysis(true);
    try {
      const res = await adminAPI.getUserAnalysis(user.id);
      if (res.data.success) {
        setUserAnalysis(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load user analysis:', error);
      alert(t('userManagement.loadAnalysisError', { defaultValue: 'Failed to load user analysis' }));
    } finally {
      setLoadingAnalysis(false);
    }
  };

  // Search units.
  const searchDevicesForBinding = async (query = '', roleOverride = targetRole, userOverride = selectedUser) => {
    setLoadingDevices(true);
    try {
      const res = await adminAPI.searchDevices({
        query,
        role: roleOverride,
        userId: userOverride?.id
      });
      if (res.data.success) {
        setAvailableDevices(res.data.data);
      }
    } catch (error) {
      console.error('Failed to search devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  const resetDeviceBindingModal = () => {
    setDeviceSelectionModalOpen(false);
    setSelectedDeviceIds([]);
    setDeviceSearchQuery('');
    setAvailableDevices([]);
    setTargetRole('');
    setSelectedUser(null);
  };

  const openDeviceBinding = (user, role) => {
    if (!canManagePartnerBindings) return;
    setSelectedUser(user);
    setTargetRole(role);
    setSelectedDeviceIds([]);
    setDeviceSearchQuery('');
    setAvailableDevices([]);
    setDeviceSelectionModalOpen(true);
    searchDevicesForBinding('', role, user);
  };

  const handleConfirmDeviceBinding = async () => {
    if (selectedDeviceIds.length === 0) {
      alert(t('userManagement.selectDeviceFirst', { defaultValue: 'Please select at least one device' }));
      return;
    }

    if (targetRole === 'Steward' && selectedDeviceIds.length > 5) {
      alert(t('userManagement.stewardDeviceLimit', { defaultValue: 'A steward can manage up to 5 devices' }));
      return;
    }

    try {
      const roleRes = await adminAPI.updateUserRole(selectedUser.id, targetRole);
      if (!roleRes.data.success) {
        alert(t('userManagement.roleUpdateFailed', { defaultValue: 'Role update failed' }));
        return;
      }

      const bindRes = await adminAPI.bindDevicesToUser(selectedUser.id, selectedDeviceIds, targetRole);
      if (bindRes.data.success) {
        alert(t('userManagement.deviceBindSuccess', {
          defaultValue: 'Successfully upgraded to {{role}} and bound {{count}} devices',
          role: t(`userManagement.roles.${targetRole}`, { defaultValue: targetRole }),
          count: selectedDeviceIds.length
        }));
        resetDeviceBindingModal();
        loadUsers();
      }
    } catch (error) {
      alert(`Operation failed: ${error.response?.data?.message || 'Unknown error'}`);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      'Super-Admin': 'bg-red-500/10 text-red-400 border-red-500/20',
      'Admin': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
      'GM': 'bg-cyan-400/10 text-cyan-400 border-cyan-400/20',
      'Finance': 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      'Business': 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      'AfterSales': 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20',
      KUD: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
      PUSKUD: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      'Steward': 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      'User': 'bg-slate-500/10 text-slate-400 border-slate-500/20',
    };
    return `px-2 py-0.5 rounded-lg text-[10px] font-bold border ${styles[role] || 'bg-slate-500/10 text-slate-500'}`;
  };

  const parseMoney = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatPhp = (value) => `₱${Math.round(parseMoney(value)).toLocaleString('en-PH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;

  const getUserPhone = (user) => user?.phone || user?.phoneNumber || '';

  const getUserDisplayName = (user) => (
    user?.displayName ||
    user?.nickname ||
    user?.name ||
    getUserPhone(user) ||
    t('userManagement.table.noName')
  );

  const getWaterTypeLabel = (waterType) => {
    const normalized = String(waterType || '').trim().toLowerCase();
    if (!normalized) {
      return t('userManagement.waterTypes.unknown', { defaultValue: 'Not recorded' });
    }
    if (['pure', 'purewater', 'pure_water'].includes(normalized)) {
      return t('userManagement.waterTypes.pure', { defaultValue: 'Pure water' });
    }
    if (['mineral', 'mineralwater', 'mineral_water'].includes(normalized)) {
      return t('userManagement.waterTypes.mineral', { defaultValue: 'Mineral water' });
    }
    return waterType;
  };

  const formatTemperature = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const numeric = Number(value);
    const formatted = Number.isFinite(numeric)
      ? numeric.toLocaleString('en-PH', { maximumFractionDigits: 1 })
      : value;
    return `${formatted} ℃`;
  };

  const getTransactionLocation = (tx) => (
    tx?.unit?.locationName ||
    tx?.unit?.location ||
    tx?.location ||
    t('userManagement.unknownLocation', { defaultValue: 'Unknown location' })
  );

  const getTransactionCardDisplay = (tx) => {
    if (tx?.cardDisplay) return tx.cardDisplay;
    if (String(tx?.cardType || '').toLowerCase() === 'virtual') {
      return t('userManagement.virtualCard', { defaultValue: 'Virtual card' });
    }
    return tx?.cardNumber || tx?.displayCardNumber || tx?.rfid || 'N/A';
  };

  const getPhysicalCardNumber = (card) => (
    card?.displayCardNumber ||
    card?.cardNumber ||
    card?.cardNo ||
    card?.rfid ||
    'N/A'
  );

  const getPhysicalCardStatusLabel = (card) => {
    if (card?.statusLabel) return card.statusLabel;
    if (card?.linked) return t('userManagement.linked', { defaultValue: 'Linked' });
    return card?.status || t('userManagement.unlinked', { defaultValue: 'Unlinked' });
  };

  const isPhysicalCardLinked = (card) => (
    Boolean(card?.linked) ||
    String(getPhysicalCardStatusLabel(card)).toLowerCase() === 'linked'
  );

  const getRechargeDelta = (recharge) => {
    const balanceBefore = parseMoney(recharge.balanceBefore ?? recharge.balance_before);
    const balanceAfter = parseMoney(recharge.balanceAfter ?? recharge.balance_after);
    return balanceAfter - balanceBefore;
  };

  const isRechargeAmountMismatch = (recharge) => {
    if (recharge.status !== 'Completed') return false;
    const balanceBefore = recharge.balanceBefore ?? recharge.balance_before;
    const balanceAfter = recharge.balanceAfter ?? recharge.balance_after;
    if (balanceBefore === null || balanceBefore === undefined || balanceBefore === '' ||
      balanceAfter === null || balanceAfter === undefined || balanceAfter === '') return false;
    return Math.abs(getRechargeDelta(recharge) - parseMoney(recharge.amount)) > 0.01;
  };

  const getRechargeField = (recharge, camelKey, snakeKey) => recharge[camelKey] ?? recharge[snakeKey];

  const getTopAnalysisEntry = (counts = {}) => {
    const [key, value] = Object.entries(counts)
      .filter(([, count]) => Number(count) > 0)
      .sort((a, b) => Number(b[1]) - Number(a[1]))[0] || [];
    return key ? { key, value: Number(value) } : null;
  };

  const getAnalysisWaterTypeLabel = (key) => {
    if (!key || key === 'unknown') {
      return t('userManagement.waterTypes.unknown', { defaultValue: 'Not recorded' });
    }
    return getWaterTypeLabel(key);
  };

  const getAnalysisCardTypeLabel = (key) => {
    if (key === 'Physical') return t('userManagement.physicalCard', { defaultValue: 'Physical card' });
    if (key === 'Virtual') return t('userManagement.virtualCard', { defaultValue: 'Virtual card' });
    return t('userManagement.analysisUnknown', { defaultValue: 'Unknown' });
  };

  const getInsightTitle = (insight) => (
    t(`userManagement.analysisInsightTitles.${insight.type}`, { defaultValue: insight.title || insight.type })
  );

  const getInsightClass = (severity) => {
    if (severity === 'warning') return 'border-amber-400/30 bg-amber-500/10 text-amber-100';
    if (severity === 'error') return 'border-red-400/30 bg-red-500/10 text-red-100';
    return 'border-cyan-400/20 bg-cyan-500/10 text-cyan-100';
  };

  const closeAnalysisModal = () => {
    setAnalysisModalOpen(false);
    setAnalysisUser(null);
    setUserAnalysis(null);
  };

  const currentPage = Math.max(1, Number(pagination.page) || 1);
  const pageLimit = Math.max(1, Number(pagination.limit) || 20);
  const totalUsers = Math.max(0, Number(pagination.total) || users.length);
  const totalPages = Math.max(1, Number(pagination.totalPages) || Math.ceil(totalUsers / pageLimit) || 1);
  const pageStart = totalUsers === 0 ? 0 : ((currentPage - 1) * pageLimit) + 1;
  const pageEnd = totalUsers === 0 ? 0 : Math.min(totalUsers, pageStart + users.length - 1);

  return (
    <div className="space-y-6 p-4 admin-text-primary md:space-y-8 md:p-8">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="flex items-center gap-3 text-2xl font-bold md:text-3xl">
            <Users className="text-cyan-400" size={32} />
            {t('userManagement.title')}
          </h2>
          <p className="admin-text-secondary mt-1 text-sm">{t('userManagement.description')}</p>
        </div>
        <div className="flex w-full flex-col gap-3 lg:w-auto lg:flex-row lg:gap-4">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              placeholder={t('userManagement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-input w-full rounded-lg py-2 pl-10 pr-4 text-sm outline-none transition-all"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="admin-input w-full rounded-lg px-4 py-2 text-sm font-semibold outline-none transition-all sm:w-44"
            aria-label={t('userManagement.filterRole', { defaultValue: 'Filter role' })}
          >
            <option value="All">{t('userManagement.allRoles', { defaultValue: 'All roles' })}</option>
            {ALL_ROLES.map(role => (
              <option key={role} value={role}>{t(`userManagement.roles.${role}`)}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={exportCurrentUsers}
            disabled={exportingCsv}
            className="admin-button-secondary flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all disabled:cursor-wait disabled:opacity-60 sm:w-auto"
          >
            {exportingCsv ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
            {exportingCsv
              ? t('userManagement.export.exporting')
              : t('userManagement.export.currentFilters')}
          </button>
          {canManageUsers && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="admin-primary-button flex w-full items-center justify-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold shadow-lg shadow-cyan-400/20 transition-all hover:scale-105 sm:w-auto"
            >
              <UserPlus size={18} />
              {t('userManagement.addMember')}
            </button>
          )}
        </div>
      </header>

      {exportError && (
        <div role="alert" className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-700 dark:text-red-200">
          {exportError}
        </div>
      )}

      {/* User List */}
      <div className="admin-card overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[880px] text-left">
          <thead className="admin-muted-panel text-xs uppercase tracking-wide admin-text-muted font-black">
            <tr>
              <th className="p-6">{t('userManagement.table.userInfo')}</th>
              <th className="p-6">{t('userManagement.table.role')}</th>
              <th className="p-6">{t('userManagement.table.balance')}</th>
              <th className="p-6 text-right">{t('userManagement.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading ? (
              <tr><td colSpan="4" className="p-12 text-center text-slate-500">{t('userManagement.table.loading')}</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="4" className="p-12 text-center admin-text-muted">{t('userManagement.table.noUsers', { defaultValue: 'No users found' })}</td></tr>
            ) : users.map((user) => (
              <React.Fragment key={user.id}>
                {/* Primary row */}
                <tr className="group cursor-pointer border-b border-[var(--admin-border-subtle)] transition-all hover:bg-[var(--admin-surface-soft)]" onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}>
                  <td className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--admin-surface-soft)] font-bold uppercase admin-text-secondary">
                        {(getUserDisplayName(user) || 'U')[0]}
                      </div>
                      <div>
                        <p className="text-xs admin-text-muted font-mono">ID: {user.id}</p>
                        <p className="font-bold admin-text-primary">{getUserDisplayName(user)}</p>
                        <p className="text-xs admin-text-muted flex items-center gap-1"><Phone size={10} /> {getUserPhone(user) || 'N/A'}</p>
                        {user.email && (
                          <p className="text-xs admin-text-muted flex items-center gap-1"><Mail size={10} /> {user.email}</p>
                        )}
                      </div>
                      <div className="ml-auto">
                        {expandedUserId === user.id ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                      </div>
                    </div>
                  </td>
                  <td className="p-6" onClick={(e) => e.stopPropagation()}>
                    {editingUserId === user.id ? (() => {
                      const draftRoles = roleDrafts[user.id] || normalizeUserRoles(user);
                      const savingRoles = savingRoleUserId === user.id;
                      const dirty = isRoleDraftDirty(user);

                      return (
                        <div className="admin-card w-full min-w-[320px] max-w-xl space-y-3 rounded-2xl p-3 shadow-xl shadow-cyan-950/10">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {ALL_ROLES.map((role) => {
                              const selected = draftRoles.includes(role);
                              const locked = role === 'User';

                              return (
                                <button
                                  key={role}
                                  type="button"
                                  disabled={savingRoles || locked}
                                  onClick={() => toggleRoleDraft(user, role)}
                                  aria-pressed={selected}
                                  className={`rounded-xl border p-3 text-left transition-all ${
                                    selected
                                      ? 'border-cyan-500/50 bg-cyan-50 text-cyan-900'
                                      : 'border-[var(--admin-border-subtle)] bg-[var(--admin-surface-soft)] admin-text-secondary hover:border-cyan-300 hover:text-cyan-800'
                                  } ${locked ? 'cursor-default opacity-90' : ''}`}
                                >
                                  <span className="flex items-start gap-3">
                                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                      selected ? 'border-cyan-300 bg-cyan-400 text-slate-950' : 'border-slate-600'
                                    }`}>
                                      {selected && <Check size={13} strokeWidth={3} />}
                                    </span>
                                    <span className="min-w-0">
                                      <span className="block text-xs font-black">{t(`userManagement.roles.${role}`)}</span>
                                      <span className="admin-text-muted mt-1 block text-[10px] leading-snug">
                                        {t(`userManagement.roleEditor.descriptions.${role}`, { defaultValue: ROLE_DESCRIPTIONS[role] })}
                                      </span>
                                    </span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>

                          <div className="flex items-start gap-2 rounded-xl border border-amber-400/30 bg-amber-50 p-3 text-xs leading-snug text-amber-800">
                            <AlertCircle size={13} className="mt-0.5 shrink-0" />
                            <span className="space-y-1">
                              <span className="block">
                                {t('userManagement.roleEditor.bindingNote', {
                                  defaultValue: 'Roles are opened here only. Bind Partner PA or steward users to devices in Units.'
                                })}
                              </span>
                              <span className="block text-amber-900">
                                {t('userManagement.roleEditor.sameAccountPolicy', {
                                  defaultValue: SAME_ACCOUNT_POLICY_DEFAULT
                                })}
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => cancelRoleEdit(user.id)}
                              disabled={savingRoles}
                              className="admin-secondary-button rounded-lg px-3 py-2 text-xs font-bold transition-colors disabled:opacity-50"
                            >
                              {t('common.cancel')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRolesSave(user)}
                              disabled={savingRoles || !dirty}
                              className="inline-flex items-center gap-2 rounded-lg bg-cyan-400 px-3 py-2 text-xs font-black text-slate-950 transition-all hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Check size={14} />
                              {savingRoles ? t('common.saving') : t('common.save')}
                            </button>
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="flex flex-wrap gap-1">
                        {normalizeUserRoles(user).map(role => (
                          <span
                            key={role}
                            className={`${getRoleBadge(role)} ${(user.activeRole === role || (user.activeRole === 'RP' && role === 'KUD')) ? 'ring-2 ring-offset-2 ring-offset-slate-800 ring-cyan-400' : ''}`}
                          >
                            {t(`userManagement.roles.${role}`)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="p-6 text-emerald-400 font-mono font-bold">
                    {formatPhp(user.balance)}
                  </td>
                  <td className="p-6 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-2">
                      {canManageUsers && (
                        <>
                          <button
                            onClick={() => beginRoleEdit(user)}
                            className="p-2 text-slate-500 hover:text-cyan-400 transition-colors"
                            title={t('userManagement.editRole')}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setPasswordModalOpen(true);
                            }}
                            className="p-2 text-slate-500 hover:text-amber-400 transition-colors"
                            title={t('userManagement.changePassword')}
                          >
                            <Key size={16} />
                          </button>
                        </>
                      )}
                      {canViewUserLogs && (
                        <button
                          onClick={() => {
                            setViewingLogsUserId(user.id);
                            setViewingLogsUserName(getUserDisplayName(user));
                          }}
                          className="p-2 text-slate-500 hover:text-purple-400 transition-colors"
                          title={t('userManagement.viewLogs')}
                        >
                          <FileText size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expanded row: summary data */}
                {expandedUserId === user.id && user.stats && (
                  <tr className="bg-[var(--admin-surface-soft)]">
                    <td colSpan="4" className="p-6">
                      {/* nickname / avatar */}
                      {(user.nickname || user.avatar) && (
                        <div className="admin-soft-panel flex items-center gap-4 mb-4 p-4 rounded-2xl">
                          {user.avatar && (
                            <img
                              src={user.avatar}
                              alt="avatar"
                              className="w-12 h-12 rounded-full object-cover border-2 border-cyan-400/30"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          {user.nickname && (
                            <div>
                              <p className="admin-text-muted text-[10px] uppercase tracking-widest">Nickname</p>
                              <p className="admin-text-primary text-sm font-bold">{user.nickname}</p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                        {/* Water-dispense summary */}
                        <div
                          className="admin-card rounded-2xl p-4 border border-cyan-400/20 cursor-pointer hover:border-cyan-400/50 transition-all hover:scale-[1.02]"
                          onClick={() => {
                            setWaterConsumptionModalOpen(true);
                            loadWaterTransactions(user.id);
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Droplet size={16} className="text-cyan-400" />
                            <span className="admin-text-secondary text-xs font-bold">{t('userManagement.dispenseStats')}</span>
                          </div>
                          <p className="admin-text-primary text-lg font-bold">{user.stats.waterPurchases.count} {t('common.times')}</p>
                          <p className="admin-text-muted text-xs">{(parseFloat(user.stats.waterPurchases.totalVolume) || 0).toFixed(1)} L / {formatPhp(user.stats.waterPurchases.totalAmount)}</p>
                        </div>

                        {/* Top-up summary */}
                        <div
                          className="admin-card rounded-2xl p-4 border border-emerald-400/20 cursor-pointer hover:border-emerald-400/50 transition-all hover:scale-[1.02]"
                          onClick={() => {
                            setRechargeModalOpen(true);
                            loadRecharges(user.id);
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Wallet size={16} className="text-emerald-400" />
                            <span className="admin-text-secondary text-xs font-bold">{t('userManagement.rechargeStats')}</span>
                          </div>
                          <p className="admin-text-primary text-lg font-bold">{user.stats.topUps.count} {t('common.times')}</p>
                          <p className="admin-text-muted text-xs">{formatPhp(user.stats.topUps.totalAmount)}</p>
                        </div>

                        {/* Bound cards */}
                        <div
                          className="admin-card rounded-2xl p-4 border border-purple-400/20 cursor-pointer hover:border-purple-400/50 transition-all hover:scale-[1.02]"
                          onClick={() => {
                            setCardsModalOpen(true);
                            loadUserCards(user.id);
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <CreditCard size={16} className="text-purple-400" />
                            <span className="admin-text-secondary text-xs font-bold">{t('userManagement.boundCards')}</span>
                          </div>
                          <p className="admin-text-primary text-lg font-bold">{user.stats.cards.total} {t('common.cards')}</p>
                          <p className="admin-text-muted text-xs">{t('common.clickToView')}</p>
                        </div>

                        {/* User rule analysis */}
                        <div
                          className="admin-card rounded-2xl p-4 border border-blue-400/20 cursor-pointer hover:border-blue-400/50 transition-all hover:scale-[1.02]"
                          onClick={() => loadUserAnalysis(user)}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <BarChart3 size={16} className="text-blue-400" />
                            <span className="admin-text-secondary text-xs font-bold">{t('userManagement.analysisStats')}</span>
                          </div>
                          <p className="admin-text-primary text-lg font-bold">{t('userManagement.analysisRuleBadge')}</p>
                          <p className="admin-text-muted text-xs">{t('userManagement.analysisClickHint')}</p>
                        </div>

                        {/* Log summary */}
                        {canViewUserLogs && (
                          <div
                            className="admin-card rounded-2xl p-4 border border-amber-400/20 cursor-pointer hover:border-amber-400/50 transition-all hover:scale-[1.02]"
                            onClick={() => {
                              setViewingLogsUserId(user.id);
                              setViewingLogsUserName(getUserDisplayName(user));
                            }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle size={16} className="text-amber-400" />
                              <span className="admin-text-secondary text-xs font-bold">{t('userManagement.appLogs')}</span>
                            </div>
                            <p className="admin-text-primary text-lg font-bold">{user.stats.logs.total} {t('common.entries')}</p>
                            <p className="admin-text-muted text-xs">{t('common.clickToView')}</p>
                          </div>
                        )}
                      </div>
                      {canManagePartnerBindings && normalizeUserRoles(user).some(role => ['Steward', 'KUD', 'PUSKUD'].includes(role)) && (
                        <div className="admin-soft-panel mt-4 rounded-2xl p-4">
                          <div className="flex flex-wrap gap-2">
                            {['Steward', 'KUD', 'PUSKUD']
                              .filter(role => normalizeUserRoles(user).includes(role))
                              .map(role => (
                                <button
                                  key={role}
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openDeviceBinding(user, role);
                                  }}
                                  className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-50 px-3 py-2 text-xs font-bold text-cyan-800 transition-colors hover:border-cyan-500"
                                  title={t('userManagement.bindDevicesForRole', {
                                    defaultValue: 'Bind devices for {{role}}',
                                    role: t(`userManagement.roles.${role}`, { defaultValue: role })
                                  })}
                                >
                                  <Monitor size={14} />
                                  {t('userManagement.bindDevicesForRole', {
                                    defaultValue: 'Bind devices for {{role}}',
                                    role: t(`userManagement.roles.${role}`, { defaultValue: role })
                                  })}
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-muted)] px-4 py-3 text-sm admin-text-secondary sm:flex-row sm:items-center sm:justify-between">
        <span>
          {t('userManagement.paginationSummary', {
            defaultValue: 'Showing {{start}}-{{end}} of {{total}} users',
            start: pageStart,
            end: pageEnd,
            total: totalUsers
          })}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => loadUsers({ page: currentPage - 1 })}
            disabled={loading || currentPage <= 1}
            className="inline-flex items-center gap-1 rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface)] px-3 py-2 text-xs font-bold transition-colors hover:border-[var(--admin-border-strong)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft size={14} />
            {t('userManagement.previousPage', { defaultValue: 'Previous' })}
          </button>
          <span className="rounded-lg bg-[var(--admin-surface)] px-3 py-2 text-xs font-bold">
            {currentPage} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => loadUsers({ page: currentPage + 1 })}
            disabled={loading || currentPage >= totalPages}
            className="inline-flex items-center gap-1 rounded-lg border border-[var(--admin-border-subtle)] bg-[var(--admin-surface)] px-3 py-2 text-xs font-bold transition-colors hover:border-[var(--admin-border-strong)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('userManagement.nextPage', { defaultValue: 'Next' })}
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

    {/* Modal */}
      {canManageUsers && isModalOpen && (
        <AdminModalSurface onClose={() => setIsModalOpen(false)}>
          <form data-testid="new-member-form" onSubmit={handleCreateUser} className="admin-card relative w-full max-w-md space-y-6 rounded-[2rem] p-6 shadow-2xl sm:rounded-[2.5rem] sm:p-8">
            <button type="button" onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 text-slate-500 hover:text-slate-900 transition-colors">
              <X size={24} />
            </button>
            <h3 className="text-2xl font-bold">{t('userManagement.modal.addTitle')}</h3>
            <p className="admin-text-secondary text-xs">{t('userManagement.modal.addSubtitle')}</p>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold ml-1">{t('userManagement.modal.nameLabel')}</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  required
                  placeholder={t('userManagement.modal.namePlaceholder')}
                  className="admin-input w-full rounded-2xl p-4 outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold ml-1">{t('userManagement.modal.phoneLabel')}</label>
                <input
                  type="tel"
                  value={newUser.phoneNumber}
                  onChange={(e) => setNewUser({...newUser, phoneNumber: e.target.value})}
                  required
                  placeholder={t('userManagement.modal.phonePlaceholder')}
                  className="admin-input w-full rounded-2xl p-4 outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold ml-1">{t('userManagement.modal.roleLabel')}</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  className="admin-input w-full rounded-2xl p-4 outline-none transition-all appearance-none"
                >
                  {ALL_ROLES.map(r => (
                    <option key={r} value={r}>{t(`userManagement.roles.${r}`)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-8 flex gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="admin-secondary-button min-w-0 flex-1 rounded-2xl px-2 py-4 text-xs font-bold leading-tight transition-all sm:text-sm"
              >
                {t('userManagement.modal.cancel')}
              </button>
              <button
                type="submit"
                className="min-w-0 flex-1 rounded-2xl bg-cyan-400 px-2 py-4 text-xs font-black leading-tight text-slate-900 shadow-lg shadow-cyan-400/20 transition-all hover:scale-105 sm:text-sm"
              >
                {t('userManagement.modal.confirm')}
              </button>
            </div>
          </form>
        </AdminModalSurface>
      )}

      {/* Password Change Modal */}
      {passwordModalOpen && selectedUser && (
        <AdminModalSurface onClose={() => setPasswordModalOpen(false)}>
          <form onSubmit={handlePasswordUpdate} className="admin-card w-full max-w-md p-8 rounded-[2.5rem] shadow-2xl space-y-6 relative">
            <button type="button" onClick={() => {
              setPasswordModalOpen(false);
              setNewPassword('');
              setConfirmPassword('');
              setSelectedUser(null);
            }} className="absolute top-6 right-6 text-slate-500 hover:text-slate-900 transition-colors">
              <X size={24} />
            </button>
            <div className="flex items-center gap-3">
              <Key className="text-amber-400" size={28} />
              <h3 className="text-2xl font-bold">{t('userManagement.changePasswordTitle')}</h3>
            </div>
            <div className="admin-soft-panel rounded-2xl p-4">
              <p className="admin-text-secondary text-sm">{t('userManagement.user')}</p>
              <p className="admin-text-primary text-lg font-bold">{getUserDisplayName(selectedUser)}</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold ml-1">{t('userManagement.newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={t('userManagement.minChars')}
                  className="admin-input w-full rounded-2xl p-4 outline-none transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold ml-1">{t('userManagement.confirmPassword')}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder={t('userManagement.repeatPassword')}
                  className="admin-input w-full rounded-2xl p-4 outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                type="button"
                onClick={() => {
                  setPasswordModalOpen(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setSelectedUser(null);
                }}
                className="admin-secondary-button flex-1 py-4 rounded-2xl font-bold text-sm transition-all"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="flex-1 bg-amber-400 text-slate-900 py-4 rounded-2xl font-black text-sm hover:scale-105 transition-all shadow-lg shadow-amber-400/20"
              >
                {t('userManagement.confirmChange')}
              </button>
            </div>
          </form>
        </AdminModalSurface>
      )}

      {/* Water Consumption Detail Modal */}
      {waterConsumptionModalOpen && (
        <AdminModalSurface onClose={() => setWaterConsumptionModalOpen(false)}>
          <div className="admin-card w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col">
            {/* Header */}
            <div className="p-8 border-b border-[var(--admin-border-subtle)] flex justify-between items-center">
              <div>
                <h3 className="admin-text-primary text-2xl font-bold flex items-center gap-3">
                  <Droplet className="text-cyan-400" size={28} />
                  {t('userManagement.dispenseDetailTitle')}
                </h3>
                <p className="admin-text-secondary text-sm mt-1">{t('userManagement.dispenseDetailSubtitle')}</p>
              </div>
              <button
                onClick={() => setWaterConsumptionModalOpen(false)}
                className="p-3 text-slate-500 hover:text-slate-800 transition-colors rounded-xl hover:bg-slate-100"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              {loadingTransactions ? (
                <div className="text-center py-12 text-slate-500">{t('common.loading')}</div>
              ) : waterTransactions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">{t('userManagement.noDispenseRecords')}</div>
              ) : (
                <div className="space-y-4">
                  {waterTransactions.map((tx) => (
                    <div key={tx.id} className="admin-soft-panel rounded-2xl p-6">
                      {/* Transaction Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="admin-text-primary font-bold text-lg">
                            {(parseFloat(tx.volume) || 0).toFixed(2)} L
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(tx.completedAt || tx.createdAt).toLocaleString('en-PH')}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-cyan-400 font-bold text-lg">
                            {formatPhp(tx.settlementAmount ?? tx.amount)}
                          </p>
                          <p className="text-xs text-slate-500">{t('userManagement.settlementAmount', { defaultValue: 'Settlement amount' })}</p>
                        </div>
                      </div>

                      {/* Water Quality Info */}
                      <div className="grid grid-cols-2 gap-4 mb-4 md:grid-cols-4">
                        <div className="admin-muted-panel rounded-xl p-3">
                          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">{t('userManagement.waterType', { defaultValue: 'Water type' })}</p>
                          <p className="admin-text-primary font-bold">{getWaterTypeLabel(tx.waterType)}</p>
                        </div>
                        <div className="admin-muted-panel rounded-xl p-3">
                          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">{t('userManagement.inputTds')}</p>
                          <p className="admin-text-primary font-bold">{tx.inputTds || 'N/A'} ppm</p>
                        </div>
                        <div className="admin-muted-panel rounded-xl p-3">
                          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">{t('userManagement.outputTds')}</p>
                          <p className="text-emerald-400 font-bold">{tx.outputTds || 'N/A'} ppm</p>
                        </div>
                        <div className="admin-muted-panel rounded-xl p-3">
                          <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">{t('userManagement.waterTemp')}</p>
                          <p className="admin-text-primary font-bold">{formatTemperature(tx.waterTemp)}</p>
                        </div>
                      </div>

                      {/* Location & Card Info */}
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-slate-500">{t('userManagement.location')}</span>
                          <span className="admin-text-primary ml-2">{getTransactionLocation(tx)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">{t('userManagement.card')}</span>
                          <span className="admin-text-primary ml-2">{getTransactionCardDisplay(tx)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">{t('userManagement.dispensingTime')}</span>
                          <span className="admin-text-primary ml-2">{tx.dispensingTime || 'N/A'}s</span>
                        </div>
                        <div>
                          <span className="text-slate-500">{t('userManagement.status')}</span>
                          <span className="text-emerald-400 ml-2">{tx.status}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </AdminModalSurface>
      )}

      {/* Recharge Detail Modal */}
      {rechargeModalOpen && (
        <AdminModalSurface onClose={() => setRechargeModalOpen(false)}>
          <div className="admin-card w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[var(--admin-border-subtle)] p-5 sm:p-8">
              <div>
                <h3 className="admin-text-primary text-2xl font-bold flex items-center gap-3">
                  <Wallet className="text-emerald-400" size={28} />
                  {t('userManagement.rechargeDetailTitle')}
                </h3>
                <p className="admin-text-secondary text-sm mt-1">{t('userManagement.rechargeDetailSubtitle')}</p>
              </div>
              <button
                onClick={() => setRechargeModalOpen(false)}
                className="p-3 text-slate-500 hover:text-slate-800 transition-colors rounded-xl hover:bg-slate-100"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-8">
              {loadingRecharges ? (
                <div className="text-center py-12 text-slate-500">{t('common.loading')}</div>
              ) : recharges.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-slate-500 mb-2">{t('userManagement.noRechargeRecords')}</div>
                  <div className="text-xs text-slate-600">{t('userManagement.rechargeHint')}</div>
                </div>
              ) : (
                <div className="space-y-4">
                  {recharges.map((recharge) => {
                    const isMismatch = isRechargeAmountMismatch(recharge);
                    const balanceBefore = getRechargeField(recharge, 'balanceBefore', 'balance_before');
                    const balanceAfter = getRechargeField(recharge, 'balanceAfter', 'balance_after');
                    const orderId = recharge.payermax_order_id || recharge.externalId;
                    const provider = recharge.payment_provider || recharge.paymentProvider;
                    const creditStatus = getRechargeField(recharge, 'creditStatus', 'credit_status');
                    const creditPresentation = getRechargeCreditPresentation(recharge);
                    const displayStatus = creditPresentation.displayStatus;
                    const walletCreditNeedsAttention = creditPresentation.needsAttention;
                    const statusClass = isMismatch || creditPresentation.tone === 'danger'
                      ? 'bg-red-500/10 text-red-400 border-red-500/30'
                      : creditPresentation.tone === 'warning'
                        ? 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                        : creditPresentation.tone === 'success'
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                          : 'bg-slate-500/10 text-[var(--admin-text-secondary)] border-[var(--admin-border-subtle)]';
                    return (
                    <div
                      key={recharge.id}
                      data-testid={`recharge-card-${recharge.id}`}
                      data-credit-state={creditPresentation.state}
                      className="admin-soft-panel rounded-2xl p-4 sm:p-6"
                    >
                      <div className="flex flex-col items-start gap-3 sm:flex-row sm:justify-between">
                        <div>
                          <p className="text-emerald-400 font-bold text-2xl">
                            {formatPhp(recharge.amount)}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(recharge.createdAt).toLocaleString('en-PH')}
                          </p>
                        </div>
                        <div className="max-w-full text-left sm:text-right">
                          <span className={`inline-flex max-w-full whitespace-normal break-words rounded-lg border px-3 py-1 text-left text-xs font-bold leading-5 ${statusClass}`}>
                            {displayStatus}
                          </span>
                        </div>
                      </div>
                      {isMismatch && (
                        <div className="mt-4 rounded-xl border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700">
                          Payment amount {formatPhp(recharge.amount)}, wallet delta {formatPhp(getRechargeDelta(recharge))}
                        </div>
                      )}
                      {walletCreditNeedsAttention && !isMismatch && (
                        <div data-testid="recharge-credit-attention" className="mt-4 rounded-xl border border-amber-400/30 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                          {t('userManagement.rechargeCreditNeedsAttention', {
                            defaultValue: 'Payment completed, but wallet credit is not confirmed yet. Check credit status and order ID before manual repair.'
                          })}
                        </div>
                      )}
                      <div className="mt-4 grid grid-cols-1 gap-3 text-xs sm:grid-cols-2 sm:gap-4">
                        <div>
                          <span className="text-slate-500">Balance before</span>
                          <span className="admin-text-primary ml-2">{formatPhp(balanceBefore)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Balance after</span>
                          <span className="admin-text-primary ml-2">{formatPhp(balanceAfter)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Wallet delta</span>
                          <span className={`ml-2 ${isMismatch ? 'text-red-600' : 'admin-text-primary'}`}>{formatPhp(getRechargeDelta(recharge))}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Credit status</span>
                          <span className="admin-text-primary ml-2">{creditStatus || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">{t('userManagement.paymentMethod')}</span>
                          <span className="admin-text-primary ml-2">{recharge.paymentMethod || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Provider</span>
                          <span className="admin-text-primary ml-2">{getPaymentProviderLabel(t, provider)}</span>
                          {String(provider || '').trim().toLowerCase() === 'manual' && (
                            <p className="mt-1 text-xs font-semibold text-amber-700">
                              {t('transactions.paymentProvider.excludedFromRevenueAndSharing')}
                            </p>
                          )}
                        </div>
                        <div>
                          <span className="text-slate-500">{t('userManagement.externalId')}</span>
                          <span className="admin-text-primary ml-2 font-mono">{orderId || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          </div>
        </AdminModalSurface>
      )}

      {/* Bound Cards Modal */}
      {cardsModalOpen && (
        <AdminModalSurface onClose={() => setCardsModalOpen(false)}>
          <div className="admin-card w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col">
            <div className="p-8 border-b border-[var(--admin-border-subtle)] flex justify-between items-center">
              <div>
                <h3 className="admin-text-primary text-2xl font-bold flex items-center gap-3">
                  <CreditCard className="text-purple-400" size={28} />
                  {t('userManagement.boundCardsTitle')}
                </h3>
                <p className="admin-text-secondary text-sm mt-1">{t('userManagement.boundCardsSubtitle')}</p>
              </div>
              <button
                onClick={() => setCardsModalOpen(false)}
                className="p-3 text-slate-500 hover:text-slate-900 transition-colors rounded-xl hover:bg-slate-100"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-6">
              {loadingCards ? (
                <div className="text-center py-12 text-slate-500">{t('common.loading')}</div>
              ) : (
                <>
                  {/* Virtual RFID */}
                  <div>
                    <h4 className="admin-text-secondary text-sm font-bold uppercase tracking-widest mb-3">{t('userManagement.virtualRfid')}</h4>
                    <div className="admin-soft-panel rounded-2xl p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-cyan-400/20 rounded-xl flex items-center justify-center">
                          <CreditCard className="text-cyan-400" size={24} />
                        </div>
                        <div>
                          <p className="admin-text-primary font-bold font-mono text-lg">{userCards.virtualRfid || 'N/A'}</p>
                          <p className="admin-text-secondary text-xs">{t('userManagement.autoCreated')}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Physical Cards */}
                  <div>
                    <h4 className="admin-text-secondary text-sm font-bold uppercase tracking-widest mb-3">
                      {t('userManagement.physicalCards')} ({userCards.physicalCards?.length || 0})
                    </h4>
                    {!userCards.physicalCards || userCards.physicalCards.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">{t('userManagement.noPhysicalCards')}</div>
                    ) : (
                      <div className="space-y-3">
                        {userCards.physicalCards.map((card) => {
                          const statusLabel = getPhysicalCardStatusLabel(card);
                          const linked = isPhysicalCardLinked(card);
                          return (
                          <div key={card.id} className="admin-soft-panel rounded-2xl p-6">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-[10px] uppercase tracking-widest text-slate-500">{t('userManagement.cardNumber', { defaultValue: 'Card No.' })}</p>
                                <p className="admin-text-primary font-bold font-mono text-lg">{getPhysicalCardNumber(card)}</p>
                                {card.rfid && card.rfid !== getPhysicalCardNumber(card) && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    {t('userManagement.chipRfid', { defaultValue: 'Chip RFID' })}: {card.rfid}
                                  </p>
                                )}
                                <p className="text-xs text-slate-500 mt-1">
                                  {t('userManagement.boundTime')} {new Date(card.boundAt).toLocaleDateString('en-PH')}
                                </p>
                              </div>
                              <span className={`px-3 py-1 rounded-lg text-xs font-bold ${
                                linked ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                                card.status === 'Inactive' ? 'bg-slate-500/10 text-slate-400 border border-slate-500/20' :
                                'bg-red-500/10 text-red-400 border border-red-500/20'
                              }`}>
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Electronic Cards */}
                  {userCards.electronicCards && userCards.electronicCards.length > 0 && (
                    <div>
                      <h4 className="admin-text-secondary text-sm font-bold uppercase tracking-widest mb-3">
                        {t('userManagement.electronicCards')} ({userCards.electronicCards.length})
                      </h4>
                      <div className="space-y-3">
                        {userCards.electronicCards.map((card) => (
                          <div key={card._id} className="admin-soft-panel rounded-2xl p-6">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="admin-text-primary font-bold">{card.cardNo}</p>
                                <p className="text-xs text-slate-500 mt-1">{t('userManagement.balance')} {formatPhp(card.balance)}</p>
                              </div>
                              <span className={`px-3 py-1 rounded-lg text-xs font-bold ${
                                card.valid === 1 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                              }`}>
                                {card.valid === 1 ? t('userManagement.active') : t('userManagement.inactive')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </AdminModalSurface>
      )}

      {/* User Analysis Modal */}
      {analysisModalOpen && (
        <AdminModalSurface onClose={closeAnalysisModal}>
          <div className="admin-card w-full max-w-5xl max-h-[90vh] rounded-[2.5rem] shadow-2xl flex flex-col">
            <div className="p-8 border-b border-[var(--admin-border-subtle)] flex justify-between items-center">
              <div>
                <h3 className="admin-text-primary text-2xl font-bold flex items-center gap-3">
                  <BarChart3 className="text-blue-400" size={28} />
                  {t('userManagement.analysisTitle')}
                </h3>
                <p className="admin-text-secondary text-sm mt-1">
                  {getUserDisplayName(analysisUser || userAnalysis?.user)} · {t('userManagement.analysisSubtitle')}
                </p>
              </div>
              <button
                onClick={closeAnalysisModal}
                className="p-3 text-slate-500 hover:text-slate-900 transition-colors rounded-xl hover:bg-slate-100"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8">
              {loadingAnalysis ? (
                <div className="text-center py-12 text-slate-500">{t('common.loading')}</div>
              ) : !userAnalysis ? (
                <div className="text-center py-12 text-slate-500">{t('userManagement.analysisNoInsights')}</div>
              ) : (() => {
                const evidence = userAnalysis.evidence || {};
                const rechargeIssueCount = Number(evidence.rechargeFailedCount || 0)
                  + Number(evidence.rechargePendingCount || 0)
                  + Number(evidence.rechargePaidNotCreditedCount || 0);
                const preferredWater = getTopAnalysisEntry(evidence.waterTypes);
                const preferredCard = getTopAnalysisEntry(evidence.cardTypes);
                const daysSince = evidence.daysSinceLastWaterPurchase;
                const hasInsufficientData = (userAnalysis.insights || []).some(insight => insight.type === 'insufficient_data');

                const metricCards = [
                  {
                    label: t('userManagement.analysisWaterPurchases'),
                    value: t('userManagement.analysisTimes', { count: evidence.waterPurchaseCount || 0 }),
                    note: `${Number(evidence.waterPurchaseTotalVolume || 0).toFixed(2)} L / ${formatPhp(evidence.waterPurchaseTotalAmount || 0)}`
                  },
                  {
                    label: t('userManagement.analysisRechargeIssues'),
                    value: t('userManagement.analysisTimes', { count: rechargeIssueCount }),
                    note: `${evidence.rechargeFailedCount || 0} failed · ${evidence.rechargePendingCount || 0} pending · ${evidence.rechargePaidNotCreditedCount || 0} paid not credited`
                  },
                  {
                    label: t('userManagement.analysisPreferredWaterType'),
                    value: preferredWater ? getAnalysisWaterTypeLabel(preferredWater.key) : t('userManagement.waterTypes.unknown'),
                    note: preferredWater ? t('userManagement.analysisTimes', { count: preferredWater.value }) : t('userManagement.analysisInsufficientData')
                  },
                  {
                    label: t('userManagement.analysisCardPreference'),
                    value: preferredCard ? getAnalysisCardTypeLabel(preferredCard.key) : t('userManagement.analysisUnknown'),
                    note: preferredCard ? t('userManagement.analysisTimes', { count: preferredCard.value }) : t('userManagement.analysisInsufficientData')
                  },
                  {
                    label: t('userManagement.analysisInactiveDays'),
                    value: daysSince === null || daysSince === undefined
                      ? t('userManagement.analysisNoLastUse')
                      : t('userManagement.analysisDays', { count: daysSince }),
                    note: `${t('userManagement.analysisPhysicalCards')}: ${evidence.physicalCardCount || 0} · ${t('userManagement.analysisVirtualCard')}: ${evidence.hasVirtualCard ? t('userManagement.analysisHasVirtualCard') : t('userManagement.analysisNoVirtualCard')}`
                  }
                ];

                return (
                  <div className="space-y-6">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
                        {t('userManagement.analysisRuleBadge')}
                      </span>
                      <span className="text-xs text-slate-500">
                        {t('userManagement.analysisRangeDays', {
                          count: evidence.rangeDays || userAnalysis.range?.days || 30
                        })}
                      </span>
                    </div>

                    {hasInsufficientData && (
                      <div className="admin-soft-panel rounded-2xl p-5">
                        <p className="admin-text-primary font-bold">{t('userManagement.analysisInsufficientData')}</p>
                        <p className="admin-text-secondary mt-1 text-sm">
                          {t('userManagement.analysisInsufficientDataDetail')}
                        </p>
                      </div>
                    )}

                    <section>
                      <h4 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">
                        {t('userManagement.analysisEvidence')}
                      </h4>
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                        {metricCards.map((metric) => (
                          <div key={metric.label} className="admin-soft-panel rounded-2xl p-4">
                            <p className="admin-text-muted text-[10px] font-bold uppercase tracking-widest">{metric.label}</p>
                            <p className="admin-text-primary mt-2 text-lg font-bold">{metric.value}</p>
                            <p className="admin-text-muted mt-1 text-xs">{metric.note}</p>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section>
                      <h4 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">
                        {t('userManagement.analysisInsights')}
                      </h4>
                      {(userAnalysis.insights || []).length === 0 ? (
                        <div className="admin-soft-panel rounded-2xl p-6 text-center admin-text-muted">
                          {t('userManagement.analysisNoInsights')}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {userAnalysis.insights.map((insight) => (
                            <div
                              key={`${insight.type}-${insight.title}`}
                              className={`rounded-2xl border p-5 ${getInsightClass(insight.severity)}`}
                            >
                              <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                <div>
                                  <p className="admin-text-primary font-bold">{getInsightTitle(insight)}</p>
                                  <p className="admin-text-secondary mt-1 text-sm">{insight.detail}</p>
                                </div>
                                <span className="rounded-full border border-[var(--admin-border-subtle)] bg-[var(--admin-surface-soft)] px-3 py-1 text-[10px] font-bold uppercase tracking-widest admin-text-secondary">
                                  {insight.severity || 'info'}
                                </span>
                              </div>
                              {insight.metrics && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {Object.entries(insight.metrics).map(([key, value]) => (
                                    <span key={key} className="rounded-lg bg-[var(--admin-surface-soft)] px-2 py-1 text-[11px] admin-text-secondary">
                                      {key}: {String(value)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  </div>
                );
              })()}
            </div>
          </div>
        </AdminModalSurface>
      )}

      {/* Log Viewer Modal */}
      {canViewUserLogs && viewingLogsUserId && (
        <LogViewerModal
          userId={viewingLogsUserId}
          userName={viewingLogsUserName}
          onClose={() => {
            setViewingLogsUserId(null);
            setViewingLogsUserName('');
          }}
        />
      )}

      {/* Device Selection Modal */}
      {deviceSelectionModalOpen && selectedUser && (
        <AdminModalSurface onClose={resetDeviceBindingModal}>
          <div className="admin-card w-full max-w-3xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col">
            <div className="p-8 border-b border-[var(--admin-border-subtle)] flex justify-between items-center">
              <div>
                <h3 className="admin-text-primary text-2xl font-bold flex items-center gap-3">
                  <Users className="text-cyan-400" size={28} />
                  {t('userManagement.deviceBindingTitle', { defaultValue: 'Select devices' })}
                </h3>
                <p className="admin-text-secondary text-sm mt-1">
                  {t('userManagement.deviceBindingSubtitle', {
                    defaultValue: 'Upgrade {{user}} to {{role}}',
                    user: getUserDisplayName(selectedUser),
                    role: t(`userManagement.roles.${targetRole}`, { defaultValue: targetRole })
                  })}
                  {targetRole === 'Steward' && ` (${t('userManagement.maxFiveDevices', { defaultValue: 'max 5 devices' })})`}
                </p>
              </div>
              <button
                onClick={resetDeviceBindingModal}
                className="p-3 text-slate-500 hover:text-slate-900 transition-colors rounded-xl hover:bg-slate-100"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Search field */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                <input
                  type="text"
                  placeholder={t('userManagement.searchDevicePlaceholder', { defaultValue: 'Search device ID, name, or location...' })}
                  value={deviceSearchQuery}
                  onChange={(e) => {
                    setDeviceSearchQuery(e.target.value);
                    searchDevicesForBinding(e.target.value);
                  }}
                  className="admin-input w-full rounded-xl py-2 pl-10 pr-4 text-sm outline-none"
                />
              </div>

              {/* Selected-unit count */}
              <div className="flex items-center justify-between text-sm">
                <span className="admin-text-secondary">
                  {t('userManagement.selectedDevices', {
                    defaultValue: 'Selected {{count}} devices',
                    count: selectedDeviceIds.length
                  })}
                  {targetRole === 'Steward' && ` / ${t('userManagement.maxFiveDevices', { defaultValue: 'max 5 devices' })}`}
                </span>
                {selectedDeviceIds.length > 0 && (
                  <button
                    onClick={() => setSelectedDeviceIds([])}
                    className="text-red-400 hover:text-red-300"
                  >
                    {t('userManagement.clearSelection', { defaultValue: 'Clear' })}
                  </button>
                )}
              </div>

              {/* Unit list */}
              <div className="flex-1 overflow-y-auto max-h-96 rounded-xl border border-[var(--admin-border-subtle)]">
                {loadingDevices ? (
                  <div className="text-center py-12 text-slate-500">Loading...</div>
                ) : availableDevices.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    {deviceSearchQuery
                      ? t('userManagement.noMatchingDevices', { defaultValue: 'No matching devices found' })
                      : t('userManagement.enterDeviceSearch', { defaultValue: 'Enter keywords to search devices' })}
                  </div>
                ) : (
                  <div className="divide-y divide-[var(--admin-border-subtle)]">
                    {availableDevices.map((device) => {
                      const deviceKey = device.id;
                      const isSelected = selectedDeviceIds.includes(deviceKey);
                      const canSelect = targetRole !== 'Steward' || selectedDeviceIds.length < 5 || isSelected;

                      return (
                        <div
                          key={device.id}
                          onClick={() => {
                            if (!canSelect) return;
                            setSelectedDeviceIds(prev =>
                              isSelected
                                ? prev.filter(id => id !== deviceKey)
                                : [...prev, deviceKey]
                            );
                          }}
                          className={`p-4 cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-cyan-50 border-l-4 border-cyan-400'
                              : canSelect
                              ? 'hover:bg-[var(--admin-surface-soft)]'
                              : 'opacity-40 cursor-not-allowed'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={!canSelect}
                                  onChange={() => {}}
                                  className="w-4 h-4 rounded border-slate-600 text-cyan-400 focus:ring-cyan-400"
                                />
                                <div>
                                  <p className="admin-text-primary font-bold font-mono">{device.deviceId}</p>
                                  <p className="admin-text-secondary text-xs">
                                    {device.deviceName || 'Unnamed'} | {device.location || 'No location'}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-lg text-xs font-bold ${
                                device.status === 'Online'
                                  ? 'bg-emerald-500/10 text-emerald-400'
                                  : 'bg-slate-500/10 text-slate-400'
                              }`}>
                                {device.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-[var(--admin-border-subtle)] flex gap-4">
              <button
                onClick={resetDeviceBindingModal}
                className="admin-secondary-button flex-1 py-3 rounded-xl font-bold text-sm transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmDeviceBinding}
                disabled={selectedDeviceIds.length === 0}
                className="flex-1 bg-cyan-400 text-slate-900 py-3 rounded-xl font-bold text-sm hover:scale-105 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('userManagement.confirmBinding', {
                  defaultValue: 'Confirm Binding ({{count}})',
                  count: selectedDeviceIds.length
                })}
              </button>
            </div>
          </div>
        </AdminModalSurface>
      )}
    </div>
  );
}
